import type { SearchSchemaInput } from '@tanstack/react-router';
import { parseEnum, validateListSearch, type ListSearch } from '@/shared/lib/listSearch';
import {
  TaskPriority,
  TaskStatusCategory,
  TaskSubjectType,
  TasksControllerListTasksV1SortBy,
  type TaskFiltersDto,
} from '@/api/generated/model';

/**
 * URL state for the Tasks screen.
 *
 * The filter half is exactly the backend's `TaskFiltersDto`, so the same
 * object goes to the board endpoint, the list endpoint, and into a saved view
 * without a translation layer. `view` names the saved view the filters came
 * from; `task` names the task whose modal is open, which is what makes a task
 * link shareable (`/tasks?task=<id>`).
 */
export type TaskLayout = 'board' | 'list';
export type TaskSortField = TasksControllerListTasksV1SortBy;

export interface TasksSearch extends Omit<ListSearch, 'sortBy' | 'search'>, TaskFiltersDto {
  layout: TaskLayout;
  sortBy?: TaskSortField | undefined;
  view?: string | undefined;
  /** An open task's id, or `new` for the create dialog. */
  task?: string | undefined;
}

/** The keys of `TasksSearch` that are filters, in the order the bar shows them. */
export const FILTER_KEYS = [
  'statusId',
  'category',
  'assigneeId',
  'unassigned',
  'teamId',
  'labelId',
  'priority',
  'creatorId',
  'subjectType',
  'subjectId',
  'dueFrom',
  'dueTo',
  'overdue',
  'includeChecklist',
  'search',
] as const satisfies ReadonlyArray<keyof TaskFiltersDto>;

export const PRIORITIES = [
  TaskPriority.urgent,
  TaskPriority.high,
  TaskPriority.medium,
  TaskPriority.low,
  TaskPriority.none,
] as const;

export const CATEGORIES = [
  TaskStatusCategory.backlog,
  TaskStatusCategory.unstarted,
  TaskStatusCategory.started,
  TaskStatusCategory.completed,
  TaskStatusCategory.canceled,
] as const;

export const SUBJECT_TYPES = [
  TaskSubjectType.player,
  TaskSubjectType.club,
  TaskSubjectType.contract,
] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `?x=a&x=b`, `?x=a,b` and TanStack's JSON arrays all become `string[]`. */
function toList(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : value === undefined ? [] : [value];
  return raw
    .flatMap((item) => (typeof item === 'string' ? item.split(',') : []))
    .map((item) => item.trim())
    .filter(Boolean);
}

function uuids(value: unknown): string[] | undefined {
  const list = toList(value).filter((id) => UUID.test(id));
  return list.length > 0 ? list : undefined;
}

function members(value: unknown): string[] | undefined {
  const list = toList(value).filter((id) => id.length <= 64);
  return list.length > 0 ? list : undefined;
}

function enums<const T extends readonly string[]>(value: unknown, allowed: T) {
  const list = toList(value).filter((item): item is T[number] => allowed.includes(item));
  return list.length > 0 ? list : undefined;
}

function flag(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

function date(value: unknown): string | undefined {
  return typeof value === 'string' && DATE.test(value) ? value : undefined;
}

function text(value: unknown, max = 255): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.slice(0, max) : undefined;
}

/** Coerces any filter-shaped object (URL or saved view) into valid filters. */
export function parseTaskFilters(raw: Record<string, unknown>): TaskFiltersDto {
  const filters: TaskFiltersDto = {
    statusId: uuids(raw.statusId),
    category: enums(raw.category, CATEGORIES),
    assigneeId: members(raw.assigneeId),
    unassigned: flag(raw.unassigned) || undefined,
    teamId: uuids(raw.teamId),
    labelId: uuids(raw.labelId),
    priority: enums(raw.priority, PRIORITIES),
    creatorId: members(raw.creatorId),
    subjectType: parseEnum(raw.subjectType, SUBJECT_TYPES),
    subjectId: text(raw.subjectId, 64),
    dueFrom: date(raw.dueFrom),
    dueTo: date(raw.dueTo),
    overdue: flag(raw.overdue) || undefined,
    // Default true, so only the deliberate `false` is kept.
    includeChecklist: flag(raw.includeChecklist) === false ? false : undefined,
    search: text(raw.search),
  };
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined),
  ) as TaskFiltersDto;
}

export function validateTasksSearch(
  search: Record<string, unknown> & SearchSchemaInput,
): TasksSearch {
  const list = validateListSearch({ ...search, limit: search.limit ?? 50 });
  return {
    page: list.page,
    limit: list.limit,
    sortOrder: list.sortOrder,
    sortBy: list.sortOrder
      ? parseEnum(search.sortBy, Object.values(TasksControllerListTasksV1SortBy))
      : undefined,
    layout: search.layout === 'list' ? 'list' : 'board',
    view: typeof search.view === 'string' && UUID.test(search.view) ? search.view : undefined,
    task:
      search.task === 'new' || (typeof search.task === 'string' && UUID.test(search.task))
        ? search.task
        : undefined,
    ...parseTaskFilters(search),
  };
}

/** Just the filters, for the API and for saving a view. */
export function filtersOf(search: TasksSearch): TaskFiltersDto {
  return parseTaskFilters(search as unknown as Record<string, unknown>);
}

/** How many filters are active — drives the "Clear" button and view dirtiness. */
export function countFilters(filters: TaskFiltersDto): number {
  return FILTER_KEYS.filter((key) => filters[key] !== undefined).length;
}

/** Order-insensitive comparison, so re-ticking the same labels is not "changed". */
export function sameFilters(a: TaskFiltersDto, b: TaskFiltersDto): boolean {
  const norm = (f: TaskFiltersDto) =>
    JSON.stringify(
      FILTER_KEYS.map((key) => {
        const value = f[key];
        return Array.isArray(value) ? [...value].sort() : (value ?? null);
      }),
    );
  return (
    norm(parseTaskFilters(a as Record<string, unknown>)) ===
    norm(parseTaskFilters(b as Record<string, unknown>))
  );
}

/** A search object with every filter cleared, for `navigate({ search })`. */
export const CLEARED_FILTERS: Record<(typeof FILTER_KEYS)[number], undefined> = Object.fromEntries(
  FILTER_KEYS.map((key) => [key, undefined]),
) as Record<(typeof FILTER_KEYS)[number], undefined>;
